import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { getAllCountries, getStates } from "../../store/actions";
import PropTypes from "prop-types";
import moment from "moment";
import FilterDateRange from "../common/FilterDateRange";
import "../AddCompany/companyWallet.css";

const STORED_DATE_FORMAT = 'YYYY-MM-DD';
const STATUS_OPTIONS = [
  { label: 'All', value: null },
  { label: 'Active', value: true },
  { label: 'Inactive', value: false },
];
const toDateValue = (value) => (value ? moment(value, STORED_DATE_FORMAT).toDate() : null);
const toStoredDate = (value) => (value ? moment(value).format(STORED_DATE_FORMAT) : null);
const CREATED_RANGE_ERROR = 'Created From date cannot be after Created To date.';

const FilterCompany = (props) => {
  const dispatch = useDispatch();
  const [countryList, setcountryList] = useState();
  const [stateList, setstateList] = useState();
  const countries = useSelector((state) => state.shipment.countries);
  const USSates = useSelector((state) => state.shipment.states);
  const [formData, setFormData] = useState({ country: props.filterData.country, state: props.filterData.state });
  const toInput = (value) => (value === null || value === undefined ? '' : String(value));
  const [walletForm, setWalletForm] = useState({
    min: toInput(props.filterData.minWalletBalance),
    max: toInput(props.filterData.maxWalletBalance),
    status: props.filterData.balanceStatus || null,
  });
  const [walletError, setWalletError] = useState('');
  const [createdForm, setCreatedForm] = useState({
    createdDateFrom: toDateValue(props.filterData.createdDateFrom),
    createdDateTo: toDateValue(props.filterData.createdDateTo),
    isActive: props.filterData.isActive ?? null,
  });
  const [createdError, setCreatedError] = useState('');

  const getCreatedRangeError = (form) =>
    form.createdDateFrom && form.createdDateTo && moment(form.createdDateFrom).isAfter(form.createdDateTo, 'day')
      ? CREATED_RANGE_ERROR
      : '';

  const handleCreatedDateChange = (date, fieldName) => {
    const next = { ...createdForm, [fieldName]: date || null };
    setCreatedForm(next);
    setCreatedError(getCreatedRangeError(next));
  };

  // Keep the panel in sync when the summary bar segment changes
  useEffect(() => {
    if (!props.showWalletFilter) return;
    setWalletForm({
      min: toInput(props.filterData.minWalletBalance),
      max: toInput(props.filterData.maxWalletBalance),
      status: props.filterData.balanceStatus || null,
    });
    setWalletError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filterData.balanceStatus, props.filterData.minWalletBalance, props.filterData.maxWalletBalance]);

  const WALLET_PRESETS = [
    { key: 'below0', label: 'Below $0', min: '', max: '', status: 'negative' },
    { key: 'range40k', label: '-$40,000 to $0', min: '-40000', max: '0', status: null },
    { key: 'owes1k', label: 'Owes over $1,000', min: '', max: '-1000', status: null },
    { key: 'exact0', label: 'Exactly $0', min: '0', max: '0', status: null },
  ];

  const isPresetActive = (preset) =>
    walletForm.min.trim() === preset.min &&
    walletForm.max.trim() === preset.max &&
    walletForm.status === preset.status;

  const parseBalance = (value) => {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getRangeError = (form) => {
    const min = parseBalance(form.min);
    const max = parseBalance(form.max);
    return min !== null && max !== null && min > max ? 'Minimum balance is higher than maximum. Swap the values or clear one.' : '';
  };

  const handleWalletChange = (name, value) => {
    const next = { ...walletForm, [name]: value };
    setWalletForm(next);
    setWalletError(getRangeError(next));
  };

  // Presets apply straight away
  const handlePreset = (preset) => {
    setWalletForm({ min: preset.min, max: preset.max, status: preset.status });
    setWalletError('');
    props?.setfilterData({
      ...props.filterData,
      minWalletBalance: parseBalance(preset.min),
      maxWalletBalance: parseBalance(preset.max),
      balanceStatus: preset.status,
      pageIndex: 1,
    });
  };

  useEffect(() => {
    dispatch(getAllCountries());
    dispatch(getStates(218));
  }, []);

  const TopUpForm = (item, name) => {
    if (name === 'country') {
      setFormData({ ...formData, state: [], [name]: item });
    } else {
      setFormData({ ...formData, [name]: item });
    }
  };

  useEffect(() => {
    if (countries?.length) {
      let data = countries.map((item) => {
        return {
          label: item.name,
          value: item.id,
        };
      });
      setcountryList(data);
    } else {
      setcountryList([]);
    }
  }, [countries]);

  useEffect(() => {
    if (USSates?.length) {
      let data = USSates.map((item) => {
        return {
          label: item.StateName,
          value: item.StateCode,
        };
      });
      setstateList(data);
    } else {
      setstateList([]);
    }
  }, [USSates]);

  const handleApplyFilter = () => {
    const resp = {
      ...props.filterData,
      country: formData?.country?.value,
      state: formData.state
    };
    if (props.showWalletFilter) {
      const error = getRangeError(walletForm);
      setWalletError(error);
      if (error) return;
      resp.minWalletBalance = parseBalance(walletForm.min);
      resp.maxWalletBalance = parseBalance(walletForm.max);
      resp.balanceStatus = walletForm.status;
      resp.pageIndex = 1;
    }
    if (props.showCreatedFilters) {
      const createdRangeError = getCreatedRangeError(createdForm);
      setCreatedError(createdRangeError);
      if (createdRangeError) return;
      resp.createdDateFrom = toStoredDate(createdForm.createdDateFrom);
      resp.createdDateTo = toStoredDate(createdForm.createdDateTo);
      resp.isActive = createdForm.isActive;
      const createdFiltersChanged =
        (props.filterData.createdDateFrom ?? null) !== resp.createdDateFrom ||
        (props.filterData.createdDateTo ?? null) !== resp.createdDateTo ||
        (props.filterData.isActive ?? null) !== resp.isActive;
      if (createdFiltersChanged) {
        resp.pageIndex = 1;
      }
    }
    props?.setfilterData(resp);
    return resp
  };

  const handleReset = () => {
    setFormData({
      ...formData,
      country: "",
      state: '',
    });
    setCreatedForm({ createdDateFrom: null, createdDateTo: null, isActive: null });
    setCreatedError('');
    if (props.showWalletFilter) {
      setWalletForm({ min: '', max: '', status: null });
      setWalletError('');
      const cleared = {
        ...props.filterData,
        country: '',
        state: [],
        searchKeywords: '',
        minWalletBalance: null,
        maxWalletBalance: null,
        balanceStatus: null,
        pageIndex: 1,
      };
      if (props.showCreatedFilters) {
        cleared.createdDateFrom = null;
        cleared.createdDateTo = null;
        cleared.isActive = null;
      }
      props?.setfilterData(cleared);
    }
  };

  return (
    <div className={props.filterData.showFilter ? 'filterContainer filterContainerZIndex show historyFilter zIndex2' : 'filterContainer historyFilter'}>
      <div className='row mb10'>
        <div className='col-sm-12 col-lg-3 mobMb-10'>
          <div className='row'>
            <div className='col-12'>
              <label className='labelHead' htmlFor="countryIdList">Country</label>
            </div>
            <div className='col-12'>
              <Select
                closeMenuOnSelect={true}
                options={countryList}
                classNamePrefix="list"
                id='countryIdList'
                name="countryIdList"
                value={formData?.country}
                className="activeSellerListInput"
                onChange={(value) => TopUpForm(value, "country")}
              />
            </div>
          </div>
        </div>
        {formData?.country?.value === 218 ? (
          <div className='col-sm-12 col-lg-3 mobMb-10'>
            <div className='row'>
              <div className='col-12'>
                <label className='labelHead' htmlFor="stateIdList">State</label>
              </div>
              <div className='col-12'>
                <Select
                  isMulti
                  closeMenuOnSelect={true}
                  options={stateList}
                  classNamePrefix="list"
                  id='stateIdList'
                  className="activeSellerListInput"
                  name="stateIdList"
                  value={formData?.state}
                  onChange={(value) => TopUpForm(value, "state")}
                  placeholder="Select state"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className='col-sm-12 col-lg-3 mobMb-10'>
            <div className='row'>
              <div className='col-12'>
                <label className='labelHead' htmlFor="stateIdList">State</label>
              </div>
              <div className='col-12'>
                <input
                  onChange={(e) => TopUpForm(e.target.value, "state")}
                  className="form-control availableBalanceInput"
                  value={
                    typeof formData?.state === "object"
                      ? formData?.state.label
                      : formData?.state
                  }
                  placeholder="Enter state"
                  type="text"
                />
              </div>
            </div>
          </div>
        )}
        {props.showWalletFilter && (
          <div className='col-sm-12 col-lg-4 mobMb-10'>
            <div className='row'>
              <div className='col-12'>
                <label className='labelHead' htmlFor="minWalletBalance">Wallet balance range</label>
              </div>
              <div className='col-12'>
                <div className='walletRangeInputs'>
                  <div className="walletMoney">
                    <span>$</span>
                    <input
                      id="minWalletBalance"
                      type="number"
                      step="0.01"
                      className="form-control availableBalanceInput"
                      placeholder="Min, e.g. -40000"
                      aria-label="Min wallet balance"
                      value={walletForm.min}
                      onChange={(e) => handleWalletChange('min', e.target.value)}
                    />
                  </div>
                  <span>to</span>
                  <div className="walletMoney">
                    <span>$</span>
                    <input
                      id="maxWalletBalance"
                      type="number"
                      step="0.01"
                      className="form-control availableBalanceInput"
                      placeholder="Max, e.g. 0"
                      aria-label="Max wallet balance"
                      value={walletForm.max}
                      onChange={(e) => handleWalletChange('max', e.target.value)}
                    />
                  </div>
                </div>
                <div className="walletPresets">
                  {WALLET_PRESETS.map((preset) => (
                    <button
                      type="button"
                      key={preset.key}
                      className={`walletPreset ${isPresetActive(preset) ? 'active' : ''}`}
                      onClick={() => handlePreset(preset)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                {walletError && <div className="walletRangeError" role="alert">{walletError}</div>}
              </div>
            </div>
          </div>
        )}
        {props.showCreatedFilters && (
          <>
            <FilterDateRange
              filterData={createdForm}
              handleDateFieldChange={handleCreatedDateChange}
              fromLabel="Created From"
              toLabel="Created To"
              fromFieldName="createdDateFrom"
              toFieldName="createdDateTo"
              fromMaxDate={createdForm.createdDateTo}
              toMinDate={createdForm.createdDateFrom}
              fromTestId="company-created-from"
              toTestId="company-created-to"
            />
            <div className='col-sm-12 col-lg-3 mobMb-10'>
              <div className='row'>
                <div className='col-12'>
                  <label className='labelHead' htmlFor="companyStatusFilter">Status</label>
                </div>
                <div className='col-12'>
                  <Select
                    closeMenuOnSelect={true}
                    options={STATUS_OPTIONS}
                    classNamePrefix="list"
                    inputId="companyStatusFilter"
                    name="companyStatusFilter"
                    className="activeSellerListInput"
                    value={STATUS_OPTIONS.find((option) => option.value === createdForm.isActive)}
                    onChange={(option) => setCreatedForm({ ...createdForm, isActive: option.value })}
                  />
                </div>
              </div>
            </div>
            {createdError && (
              <div className='col-12 mobMb-10'>
                <div className="walletRangeError" role="alert">{createdError}</div>
              </div>
            )}
          </>
        )}
        <div className='col-sm-12 col-lg-3 mobMb-10'>
          <div className='row'>
            <div className='col-12'>
              <label className='labelHead hidLbl' htmlFor="hiddenInput">1</label>
            </div>
            <div className='col-12 flexEnd'>
              <button
                className="btn cus-primary-transparent-btn top-btn"
                onClick={handleReset}
              >
                Clear Filter
              </button>
              <button
                className="cus-seconday-bg-btn btn filterBtn"
                onClick={handleApplyFilter}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

FilterCompany.propTypes = {
  filterData: PropTypes.object.isRequired,
  setfilterData: PropTypes.func.isRequired,
  showWalletFilter: PropTypes.bool,
  showCreatedFilters: PropTypes.bool,
};

export default FilterCompany;